import { createContext, useCallback, useEffect, useState } from 'react';
import { EadNode } from 'types';
import { getNodeId } from 'utils/ead-utils';
import {
  createFlatEadTree,
  getAllNodeChildren,
  isSame,
  mergeTrees,
} from './utils/ead-tree-context-utils';
import { noop } from 'lodash';
import { fetchEadChildren } from 'components/grid/grid-api';

export type EadSimple = {
  id: string;
  nlevel: number;
  title: string;
  children: EadSimple[];
  parent: EadSimple;
  childrenCount: number;
  hasChildren: boolean;
  baseIdentifier: string;
  level: string;
  levelLabel: string;
};

export const EadTree = createContext<{
  flatEad: EadSimple[];
  setFlatEad: React.Dispatch<React.SetStateAction<EadSimple[]>>;
  expanded: Record<string, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  addChildren: (children: EadSimple[]) => void;
  removeChildren: (parent: EadSimple) => void;
  collapseAll: () => void;
  expandNext: (nodeId: string) => void;
}>({
  collapseAll: noop,
  removeChildren: noop,
  addChildren: noop,
  expandNext: noop,
  setFlatEad: noop,
  setExpanded: noop,
  expanded: {},
  flatEad: [],
  loading: false,
  setLoading: noop,
});

const expandAllParents = (ead: EadSimple): { [key: string]: true } => {
  let p = ead.parent;
  let fallback = 1000;
  const r: { [key: string]: true } = {};
  while (p && fallback > 0) {
    if (p) {
      const id = getNodeId(p);
      if (id) r[id] = true;
    }
    fallback--;
    p = p.parent;
  }

  return r;
};

export const useEadTree = (initialEad?: EadNode) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [flatEad, setFlatEad] = useState<EadSimple[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialEad) {
      setFlatEad((p) => {
        if (!p || p.length === 0) {
          return createFlatEadTree((initialEad as unknown) as EadSimple);
        } else {
          return mergeTrees(
            p,
            createFlatEadTree((initialEad as unknown) as EadSimple)
          );
        }
      });

      setExpanded((p) => ({
        ...p,
        [getNodeId((initialEad as unknown) as EadSimple) ?? '']: true, // [initialEad]
        ...expandAllParents((initialEad as unknown) as EadSimple),
      }));
    }
  }, [initialEad]);

  const collapseAll = useCallback(() => {
    setFlatEad?.((p) => p?.filter((n) => n.nlevel < 2));
    setExpanded?.({});
  }, []);

  const addChildren = useCallback((children: EadSimple[]) => {
    setFlatEad((p) => {
      const newTree = p ? [...p] : [];
      children.forEach((c) => {
        const entryIndex = newTree.findIndex((x) => isSame(x, c));
        if (entryIndex > -1) {
          newTree.splice(
            entryIndex + 1,
            0,
            ...c.children.map((child) => ({
              ...child,
              nlevel: newTree[entryIndex].nlevel + 1,
            }))
          );
        }
      });
      setExpanded((p) => ({
        ...p,
        ...Object.fromEntries(
          children.map((child) => [getNodeId(child), true])
        ),
      }));
      return newTree;
    });
  }, []);

  const removeChildren = useCallback((parent: EadSimple) => {
    setFlatEad((p) => {
      if (!p) {
        return [];
      }
      const { children, startIndex } = getAllNodeChildren(p ?? [], parent);

      //console.log('all parent children: ', parent, children, startIndex);

      setExpanded((exp) => ({
        ...exp,
        [getNodeId(parent) ?? '']: false,
        ...Object.fromEntries(
          children.map((child) => [getNodeId(child), false])
        ),
      }));

      return [
        ...p.slice(0, startIndex + 1),
        ...p.slice(startIndex + children.length + 1),
      ];
    });
  }, []);

  const expandNext = useCallback(
    async (nodeId: string) => {
      setLoading(true);

      const fromNode = flatEad.find((p) =>
        isSame(p, { id: nodeId, baseIdentifier: nodeId } as EadSimple)
      );

      if (!fromNode) {
        console.log('NODE not found:');
        console.log('nodeId = ', nodeId);
        console.log('flatEad = ', flatEad);
        setLoading(false);
        return;
      }

      const expandable = getAllNodeChildren(flatEad, fromNode)
        .children.filter((p) => p.hasChildren || p.childrenCount > 0)
        .filter((p) => getAllNodeChildren(flatEad, p).children.length === 0);

      const toAdd = await fetchEadChildren(
        expandable.map((p) => getNodeId(p) ?? p.id)
      );

      if (toAdd && toAdd.length > 0) {
        addChildren((toAdd as unknown) as EadSimple[]);
      }
      setLoading(false);
    },
    [flatEad, addChildren]
  );

  return {
    expanded,
    setExpanded,
    flatEad,
    setFlatEad,
    removeChildren,
    addChildren,
    collapseAll,
    expandNext,
    loading,
    setLoading,
  };
};
